import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { VehicleFormDialog } from '@/components/fleet/VehicleFormDialog';
import { VehiclePhotoPanel } from '@/components/fleet/VehiclePhotoPanel';
import { formatVehicleDate } from '@/components/fleet/formatVehicleDate';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { useTechnicians } from '@/routes/dispatch/useDispatchBoard';
import {
  TOOL_CONDITIONS,
  VehicleAssignmentClashError,
  useDeleteVehicleAssignment,
  useDeleteVehicleTool,
  useSaveVehicleAssignment,
  useSaveVehicleTool,
  useVehicle,
  useVehicleAssignments,
  useVehicleTools,
  type SaveVehicleToolRequest,
  type VehicleAssignment,
  type VehicleTool,
} from '@/routes/fleet/useFleet';

/**
 * One vehicle: its record, its drivers over time, and what it carries (M1-27).
 *
 * <b>Assignment history is on the page, not behind a toggle.</b> The AC asks
 * for it to be preserved, and a history nobody can see is indistinguishable
 * from one that was not kept — "who was driving Van 3 in March" is the question
 * it exists to answer, and it is asked after the fact.
 */
export function FleetVehicleDetail() {
  const { t } = useTranslation();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const vehicle = useVehicle(vehicleId);
  // See Fleet.tsx: reading the fleet and changing it are two permissions, and
  // a Dispatcher holds only the first.
  const canManage = useHasPermission()(Permissions.manageUsers);
  const [editing, setEditing] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  if (vehicle.isPending) {
    return <LoadingState label={t('fleet.detail.loading')} />;
  }
  if (vehicle.isError || !vehicle.data) {
    return (
      <ErrorState title={t('fleet.detail.loadErrorTitle')} onRetry={() => void vehicle.refetch()} />
    );
  }

  const record = vehicle.data;

  return (
    <section className="space-y-6">
      <div>
        <Link to="/fleet" className="text-sm underline underline-offset-2">
          {t('fleet.detail.backToFleet')}
        </Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{record.label}</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {[
                t(`fleet.type.${record.type}`),
                [record.year == null ? null : String(record.year), record.make, record.model]
                  .filter(Boolean)
                  .join(' '),
                record.plate,
                record.vin,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <p
              className={
                record.status === 'Active'
                  ? 'mt-1 text-sm text-[var(--color-muted-foreground)]'
                  : 'mt-1 text-sm font-medium text-[var(--color-destructive)]'
              }
              data-vehicle-status={record.status}
            >
              {t(`fleet.status.${record.status}`)}
              {/* ⚠️ Says what in_maintenance DOES, not just that it is set.
                  The AC's warning fires on the dispatch board, which is a
                  different screen from this one — an office manager flipping
                  this switch should be told what they have just caused, or the
                  consequence only shows up somewhere they are not looking. */}
              {record.status === 'InMaintenance' ? (
                <span className="ml-2 font-normal text-[var(--color-muted-foreground)]">
                  {t('fleet.detail.maintenanceExplains')}
                </span>
              ) : null}
            </p>
          </div>
          {canManage ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('fleet.detail.edit')}
            </Button>
          ) : null}
        </div>
        {record.notes ? <p className="mt-2 text-sm">{record.notes}</p> : null}
      </div>

      <VehiclePhotoPanel vehicle={record} canManage={canManage} onAnnounce={setAnnouncement} />

      <AssignmentsPanel
        vehicleId={record.id}
        canManage={canManage}
        onAnnounce={setAnnouncement}
      />
      <ToolsPanel vehicleId={record.id} canManage={canManage} onAnnounce={setAnnouncement} />

      {editing ? (
        <VehicleFormDialog
          vehicle={record}
          onClose={() => setEditing(false)}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {/* ⚠️ This screen carries the most mutations in the story — the vehicle,
          assignments, tools, and two removals — and shipped without any way to
          announce one. The axe scans were green throughout, because axe cannot
          check that something was announced. Same shape as
          TechnicianProfileDetail's. */}
      <p role="status" aria-live="polite" className="sr-only" data-testid="fleet-announcement">
        {announcement}
      </p>
    </section>
  );
}

// --- Assignments ------------------------------------------------------------

function AssignmentsPanel({
  vehicleId,
  canManage,
  onAnnounce,
}: {
  vehicleId: string;
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const assignments = useVehicleAssignments(vehicleId);
  const [editing, setEditing] = useState<VehicleAssignment | 'new' | null>(null);
  const [removing, setRemoving] = useState<VehicleAssignment | null>(null);
  const remove = useDeleteVehicleAssignment(vehicleId);

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('fleet.assignments.heading')}</h2>
        {canManage ? (
          <Button variant="secondary" onClick={() => setEditing('new')}>
            {t('fleet.assignments.add')}
          </Button>
        ) : null}
      </div>

      {assignments.isPending ? (
        <LoadingState label={t('fleet.assignments.loading')} />
      ) : assignments.isError ? (
        <ErrorState
          title={t('fleet.assignments.loadErrorTitle')}
          onRetry={() => void assignments.refetch()}
        />
      ) : assignments.data.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('fleet.assignments.empty')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="vehicle-assignments">
          {assignments.data.map((assignment) => (
            <li key={assignment.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="text-sm font-medium">{assignment.userName}</span>
                {assignment.isPrimaryDriver ? (
                  <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                    {t('fleet.assignments.primary')}
                  </span>
                ) : null}
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {assignment.effectiveTo
                    ? t('fleet.assignments.between', {
                        from: formatVehicleDate(assignment.effectiveFrom),
                        to: formatVehicleDate(assignment.effectiveTo),
                      })
                    : t('fleet.assignments.since', {
                        from: formatVehicleDate(assignment.effectiveFrom),
                      })}
                </p>
              </div>
              {canManage ? (
                <div className="flex gap-2">
                  {/* "Edit" is the prominent action and "Remove" the
                      destructive one, deliberately: ending an assignment is
                      setting its end date, which is what preserves the history
                      the AC asks for, while removing is for a row typed
                      against the wrong technician. The API cannot tell those
                      apart — this is where the difference is made legible. */}
                  <Button variant="secondary" onClick={() => setEditing(assignment)}>
                    {t('fleet.assignments.edit')}
                  </Button>
                  {/* ⚠️ `remove` is hoisted above this dialog and OUTLIVES it,
                      so a failed removal leaves isError true and the NEXT row's
                      dialog opens already announcing a failure nobody caused.
                      The same trap the three technician sections each
                      document. */}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      remove.reset();
                      setRemoving(assignment);
                    }}
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <AssignmentDialog
          vehicleId={vehicleId}
          assignment={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          titleId="remove-assignment"
          title={t('fleet.assignments.removeTitle')}
          description={t('fleet.assignments.removeDescription', { name: removing.userName })}
          confirmLabel={t('common.remove')}
          isPending={remove.isPending}
          // ⚠️ Without this a refused DELETE was swallowed in silence: the
          // dialog stayed open, Remove re-enabled, and nothing was rendered or
          // announced. Every other ConfirmDialog in this app passes it.
          errorMessage={remove.isError ? t('fleet.assignments.removeFailed') : undefined}
          testId="remove-assignment-dialog"
          onConfirm={() =>
            remove.mutate(removing.id, {
              onSuccess: () => {
                // This component does the t() call and hands over a finished
                // string — web/CONVENTIONS.md's M1-30 note.
                onAnnounce(t('fleet.assignments.removed', { name: removing.userName }));
                setRemoving(null);
              },
            })
          }
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}

function AssignmentDialog({
  vehicleId,
  assignment,
  onClose,
  onAnnounce,
}: {
  vehicleId: string;
  assignment?: VehicleAssignment;
  onClose: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const technicians = useTechnicians();
  const save = useSaveVehicleAssignment(vehicleId, assignment?.id);
  const [error, setError] = useState('');

  const [userId, setUserId] = useState(assignment?.userId ?? '');
  const [isPrimaryDriver, setIsPrimaryDriver] = useState(assignment?.isPrimaryDriver ?? false);
  const [effectiveFrom, setEffectiveFrom] = useState(assignment?.effectiveFrom ?? '');
  const [effectiveTo, setEffectiveTo] = useState(assignment?.effectiveTo ?? '');

  function submit() {
    if (!userId || !effectiveFrom) {
      setError(t('fleet.assignments.requiredFields'));
      return;
    }
    setError('');
    save.mutate(
      { userId, isPrimaryDriver, effectiveFrom, effectiveTo: effectiveTo || null },
      {
        onSuccess: (saved) => {
          onAnnounce(
            assignment
              ? t('fleet.assignments.saved', { name: saved.userName })
              : t('fleet.assignments.created', { name: saved.userName }),
          );
          onClose();
        },
        onError: (err) =>
          // ⚠️ The server's OWN wording for a clash, not one invented here.
          // There are two distinct cross-row rules behind that 400 — already
          // in another vehicle, and this vehicle already has a primary driver
          // — and they need different fixes. One generic "that clashes" would
          // tell the office something is wrong without saying what to do.
          setError(
            err instanceof VehicleAssignmentClashError
              ? err.message
              : t('fleet.assignments.saveFailed'),
          ),
      },
    );
  }

  return (
    <Dialog
      titleId={titleId}
      title={assignment ? t('fleet.assignments.editTitle') : t('fleet.assignments.addTitle')}
      onClose={onClose}
      testId="assignment-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          {/* Explicit htmlFor/id, never a wrapping label — web/CONVENTIONS.md's
              M1-38 rule. */}
          <label htmlFor={`${fieldId}-user`} className="text-sm font-medium">
            {t('fleet.assignments.technician')}
          </label>
          <select
            id={`${fieldId}-user`}
            className={FIELD_CLASS}
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">{t('fleet.assignments.choose')}</option>
            {(technicians.data ?? []).map((technician) => (
              <option key={technician.id} value={technician.id}>
                {technician.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isPrimaryDriver}
              onChange={(event) => setIsPrimaryDriver(event.target.checked)}
            />
            {t('fleet.assignments.primaryDriver')}
          </label>
          {/* Said here because this checkbox is the only thing that decides
              whether the maintenance warning ever fires for this person. */}
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {t('fleet.assignments.primaryDriverHint')}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            {/* ⚠️ `<input type="date">` has NO textbox role — queried by label
                in the tests, the trap M1-37's section records. */}
            <label htmlFor={`${fieldId}-from`} className="text-sm font-medium">
              {t('fleet.assignments.from')}
            </label>
            <input
              id={`${fieldId}-from`}
              type="date"
              className={FIELD_CLASS}
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor={`${fieldId}-to`} className="text-sm font-medium">
              {t('fleet.assignments.to')}
            </label>
            <input
              id={`${fieldId}-to`}
              type="date"
              className={FIELD_CLASS}
              value={effectiveTo}
              onChange={(event) => setEffectiveTo(event.target.value)}
            />
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {t('fleet.assignments.toHint')}
            </p>
          </div>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// --- Tools ------------------------------------------------------------------

function ToolsPanel({
  vehicleId,
  canManage,
  onAnnounce,
}: {
  vehicleId: string;
  canManage: boolean;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const tools = useVehicleTools(vehicleId);
  const [editing, setEditing] = useState<VehicleTool | 'new' | null>(null);
  const [removing, setRemoving] = useState<VehicleTool | null>(null);
  const remove = useDeleteVehicleTool(vehicleId);

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('fleet.tools.heading')}</h2>
        {canManage ? (
          <Button variant="secondary" onClick={() => setEditing('new')}>
            {t('fleet.tools.add')}
          </Button>
        ) : null}
      </div>

      {tools.isPending ? (
        <LoadingState label={t('fleet.tools.loading')} />
      ) : tools.isError ? (
        <ErrorState title={t('fleet.tools.loadErrorTitle')} onRetry={() => void tools.refetch()} />
      ) : tools.data.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('fleet.tools.empty')}</p>
      ) : (
        <ul className="space-y-2" data-testid="vehicle-tools">
          {tools.data.map((tool) => (
            <li key={tool.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="text-sm font-medium">{tool.name}</span>
                {tool.serial ? (
                  <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">
                    {tool.serial}
                  </span>
                ) : null}
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {t(`fleet.condition.${tool.condition}`)}
                  {tool.notes ? ` · ${tool.notes}` : ''}
                </p>
              </div>
              {canManage ? (
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setEditing(tool)}>
                    {t('fleet.tools.edit')}
                  </Button>
                  {/* Hoisted mutation, same trap — see the assignment list. */}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      remove.reset();
                      setRemoving(tool);
                    }}
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <ToolDialog
          vehicleId={vehicleId}
          tool={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onAnnounce={onAnnounce}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          titleId="remove-tool"
          title={t('fleet.tools.removeTitle')}
          description={t('fleet.tools.removeDescription', { name: removing.name })}
          confirmLabel={t('common.remove')}
          isPending={remove.isPending}
          errorMessage={remove.isError ? t('fleet.tools.removeFailed') : undefined}
          testId="remove-tool-dialog"
          onConfirm={() =>
            remove.mutate(removing.id, {
              onSuccess: () => {
                onAnnounce(t('fleet.tools.removed', { name: removing.name }));
                setRemoving(null);
              },
            })
          }
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}

function ToolDialog({
  vehicleId,
  tool,
  onClose,
  onAnnounce,
}: {
  vehicleId: string;
  tool?: VehicleTool;
  onClose: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const save = useSaveVehicleTool(vehicleId, tool?.id);
  const [error, setError] = useState('');

  const [form, setForm] = useState<SaveVehicleToolRequest>({
    name: tool?.name ?? '',
    serial: tool?.serial ?? null,
    condition: (tool?.condition as SaveVehicleToolRequest['condition']) ?? 'Good',
    notes: tool?.notes ?? null,
  });

  function submit() {
    const name = form.name.trim();
    if (!name) {
      setError(t('fleet.tools.nameRequired'));
      return;
    }
    setError('');
    save.mutate(
      { ...form, name },
      {
        onSuccess: (saved) => {
          onAnnounce(
            tool
              ? t('fleet.tools.saved', { name: saved.name })
              : t('fleet.tools.created', { name: saved.name }),
          );
          onClose();
        },
        onError: () => setError(t('fleet.tools.saveFailed')),
      },
    );
  }

  return (
    <Dialog
      titleId={titleId}
      title={tool ? t('fleet.tools.editTitle') : t('fleet.tools.addTitle')}
      onClose={onClose}
      testId="tool-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor={`${fieldId}-name`} className="text-sm font-medium">
            {t('fleet.tools.name')}
          </label>
          <input
            id={`${fieldId}-name`}
            className={FIELD_CLASS}
            value={form.name}
            onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`${fieldId}-serial`} className="text-sm font-medium">
              {t('fleet.tools.serial')}
            </label>
            <input
              id={`${fieldId}-serial`}
              className={FIELD_CLASS}
              value={form.serial ?? ''}
              onChange={(event) =>
                setForm((f) => ({ ...f, serial: event.target.value || null }))
              }
            />
          </div>
          <div>
            <label htmlFor={`${fieldId}-condition`} className="text-sm font-medium">
              {t('fleet.tools.condition')}
            </label>
            <select
              id={`${fieldId}-condition`}
              className={FIELD_CLASS}
              value={form.condition}
              onChange={(event) =>
                setForm((f) => ({
                  ...f,
                  condition: event.target.value as SaveVehicleToolRequest['condition'],
                }))
              }
            >
              {TOOL_CONDITIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {t(`fleet.condition.${condition}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor={`${fieldId}-notes`} className="text-sm font-medium">
            {t('fleet.tools.notes')}
          </label>
          <textarea
            id={`${fieldId}-notes`}
            className={FIELD_CLASS}
            rows={2}
            value={form.notes ?? ''}
            onChange={(event) => setForm((f) => ({ ...f, notes: event.target.value || null }))}
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
